// src/PaymentsPage.js - Group payments by user
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Chip,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
  Grid,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { supabase } from './supabaseClient';
import ProfileDialog from './ProfileDialog';
import { normalizeProfileImage } from './utils/imageUtils';

import { prettyStatus } from './labels';
export default function PaymentsPage() {
  const [groupedPayments, setGroupedPayments] = useState([]);
  const [allGroupedPayments, setAllGroupedPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogPhone, setDialogPhone] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [capturingId, setCapturingId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // payment_intent is the real transaction ledger (amount, currency,
      // status, stripe_payment_intent_id) — the `payments` table this page
      // used to query is actually a saved-card table (card_brand/last4,
      // no amount or status column at all), which is why every amount
      // showed $0.00/N/A and only 1-2 rows ever appeared platform-wide.
      let { data, error: queryError } = await supabase
        .from('payment_intent')
        .select('*')
        .order('created_at', { ascending: false });

      if (queryError) {
        console.error('Error fetching payments:', queryError);
        setError(`Error: ${queryError.message}`);
        setGroupedPayments([]);
        setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        setGroupedPayments([]);
        setLoading(false);
        return;
      }

      // Resolve which client each payment belongs to via its booking —
      // payment_intent itself has no phone_number, only booking_id/customer_id.
      const bookingIds = [...new Set(data.map(p => p.booking_id).filter(Boolean))];
      const bookingMap = new Map();
      if (bookingIds.length > 0) {
        const { data: bookings } = await supabase
          .from('bookings')
          .select('id, phone_number, client_id, service_title')
          .in('id', bookingIds);
        (bookings || []).forEach(b => bookingMap.set(b.id, b));
      }

      // Now fetch profiles for each unique phone number
      const phoneNumbers = [...new Set(
        data.map(p => bookingMap.get(p.booking_id)?.phone_number).filter(Boolean)
      )];
      const profileMap = new Map();

      if (phoneNumbers.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image, user_type, identity_verified')
          .in('phone_number', phoneNumbers);

        if (!profilesError && profiles) {
          profiles.forEach(profile => {
            profileMap.set(profile.phone_number, {
              ...profile,
              profile_image_url: normalizeProfileImage(profile.profile_image),
            });
          });
        }
      }

      // Group payments by the client's phone number (via their booking)
      const paymentsByUser = new Map();

      data.forEach(payment => {
        const booking = bookingMap.get(payment.booking_id);
        const phone = booking?.phone_number || 'Unknown';
        const profile = profileMap.get(phone) || {};

        if (!paymentsByUser.has(phone)) {
          paymentsByUser.set(phone, {
            phone,
            profile: profile,
            payments: [],
            totalAmount: 0,
          });
        }

        const group = paymentsByUser.get(phone);
        group.payments.push({ ...payment, service_title: booking?.service_title });
        if (payment.status === 'succeeded' && payment.amount) {
          group.totalAmount += parseFloat(payment.amount) || 0;
        }
      });

      // Convert to array and sort
      const grouped = Array.from(paymentsByUser.values())
        .map(group => ({
          ...group,
          payments: group.payments.sort((a, b) => 
            new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at)
          ),
        }))
        .sort((a, b) => 
          new Date(b.payments[0]?.created_at || b.payments[0]?.updated_at) - 
          new Date(a.payments[0]?.created_at || a.payments[0]?.updated_at)
        );

      setGroupedPayments(grouped);
      setAllGroupedPayments(grouped);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(`Error: ${err.message || 'Failed to fetch payments'}`);
      setGroupedPayments([]);
      setAllGroupedPayments([]);
    } finally {
      setLoading(false);
    }
  };

  // Manual fallback for the case the automatic capture (fired when a
  // stylist marks the booking Completed) didn't happen or failed — an
  // expired hold, a transient Stripe error, or a booking that was marked
  // Completed before that auto-capture existed. Only offered while the
  // PaymentIntent is actually in 'requires_capture' — the one state Stripe
  // will let this succeed from.
  const capturePayment = async (payment) => {
    if (!payment.booking_id) {
      setError('This payment has no linked booking_id to capture against.');
      return;
    }
    setCapturingId(payment.id);
    try {
      const { data, error: captureError } = await supabase.functions.invoke('capture-payment', {
        body: { booking_id: String(payment.booking_id), payment_intent_id: payment.stripe_payment_intent_id },
      });
      if (captureError) throw captureError;
      if (data?.error) throw new Error(data.error);
      await fetchData();
    } catch (err) {
      console.error('Capture payment failed:', err);
      let detail = err.message;
      try {
        const body = await err.context?.json();
        if (body?.error) detail = body.error;
      } catch (_) {
        // context wasn't JSON or wasn't readable — fall back to err.message
      }
      setError(`Capture failed: ${detail}`);
    } finally {
      setCapturingId(null);
    }
  };

  // Filter and search
  useEffect(() => {
    let filtered = [...allGroupedPayments];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(group => {
        const matchesName = `${group.profile?.first_name || ''} ${group.profile?.last_name || ''}`.toLowerCase().includes(term);
        const matchesPhone = group.phone?.toLowerCase().includes(term);
        const matchesEmail = group.profile?.email?.toLowerCase().includes(term);
        const matchesPayment = group.payments.some(p =>
          p.stripe_payment_intent_id?.toLowerCase().includes(term) ||
          p.service_title?.toLowerCase().includes(term)
        );
        return matchesName || matchesPhone || matchesEmail || matchesPayment;
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.map(group => ({
        ...group,
        payments: group.payments.filter(p => p.status === statusFilter)
      })).filter(group => group.payments.length > 0);
    }

    setGroupedPayments(filtered);
  }, [searchTerm, statusFilter, allGroupedPayments]);

  useEffect(() => {
    fetchData();
  }, []);

  const closeProfile = () => setDialogPhone(null);

  const toggleRow = (rowId) => {
    const rowIdString = String(rowId);
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIdString)) {
      newExpanded.delete(rowIdString);
    } else {
      newExpanded.add(rowIdString);
    }
    setExpandedRows(newExpanded);
  };

  const getUserTypeColor = (userType) => {
    switch (userType?.toLowerCase()) {
      case 'stylist':
        return 'primary';
      case 'client':
        return 'success';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all';

  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Payments ({groupedPayments.reduce((sum, g) => sum + g.payments.length, 0)})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage payments
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={fetchData}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by name, phone, email, card..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="succeeded">Succeeded</MenuItem>
              <MenuItem value="requires_payment_method">Requires Payment Method</MenuItem>
              <MenuItem value="requires_confirmation">Requires Confirmation</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {groupedPayments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Payments Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No payments found in the database.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          {groupedPayments.map((group, idx) => {
            const rowId = group.phone || `user-${idx}`;
            const isExpanded = expandedRows.has(String(rowId));
            const userName = `${group.profile?.first_name || ''} ${group.profile?.last_name || ''}`.trim() || 'Unknown';
            const profileImage = group.profile?.profile_image_url || group.profile?.profile_image;
            const isVerified = group.profile?.identity_verified === true || group.profile?.identity_verified === 'true' || group.profile?.identity_verified === 1;

            return (
              <Box key={rowId}>
                {/* Summary Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => toggleRow(rowId)}
                >
                  {/* Profile Image */}
                  <Box sx={{ mr: 2, flexShrink: 0 }}>
                    {profileImage ? (
                      <Avatar
                        src={profileImage}
                        alt={userName}
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
                        {userName !== 'Unknown' ? userName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    )}
                  </Box>

                  {/* User Info */}
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, flexShrink: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {userName}
                      </Typography>
                      {isVerified && (
                        <VerifiedIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, flexShrink: 0 }}>
                      {group.profile?.email || 'N/A'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 150, flexShrink: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (group.phone) setDialogPhone(group.phone);
                      }}
                    >
                      {group.phone || 'N/A'}
                    </Typography>
                    <Chip
                      label={prettyStatus(group.profile?.user_type) || 'N/A'}
                      color={getUserTypeColor(group.profile?.user_type)}
                      size="small"
                      sx={{ minWidth: 80, flexShrink: 0 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, flexShrink: 0 }}>
                      {group.payments.length} payment{group.payments.length !== 1 ? 's' : ''}
                    </Typography>
                    {group.totalAmount > 0 && (
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100, flexShrink: 0 }}>
                        ${group.totalAmount.toFixed(2)}
                      </Typography>
                    )}
                  </Box>

                  {/* Expand/Collapse Icon */}
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Payments */}
                <Collapse in={isExpanded}>
                  <Box sx={{ p: 3, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Payments ({group.payments.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {group.payments.map((payment) => (
                        <Grid item xs={12} key={payment.id}>
                          <Paper elevation={1} sx={{ p: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Service
                                </Typography>
                                <Typography variant="body2">
                                  {payment.service_title || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Stripe Payment Intent
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                  {payment.stripe_payment_intent_id || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Status
                                </Typography>
                                <Chip
                                  label={prettyStatus(payment.status) || 'N/A'}
                                  size="small"
                                  color={payment.status === 'succeeded' ? 'success' : payment.status === 'canceled' ? 'error' : 'default'}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Amount
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {(payment.currency || 'USD').toUpperCase()} ${parseFloat(payment.amount || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Created
                                </Typography>
                                <Typography variant="body2">
                                  {payment.created_at
                                    ? new Date(payment.created_at).toLocaleDateString()
                                    : 'N/A'}
                                </Typography>
                              </Grid>
                              {payment.status === 'requires_capture' && (
                                <Grid item xs={12}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    disabled={capturingId === payment.id}
                                    onClick={() => capturePayment(payment)}
                                  >
                                    {capturingId === payment.id ? 'Capturing…' : 'Capture Payment'}
                                  </Button>
                                </Grid>
                              )}
                            </Grid>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Paper>
      )}

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={closeProfile}
      />
    </Box>
  );
}

